import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { confirmCustomerPasswordReset, requestCustomerPasswordReset } from "./applyProfileApi";

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { uid, token } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      uid: (params.get("uid") || "").trim(),
      token: (params.get("token") || "").trim(),
    };
  }, [location.search]);

  const hasResetToken = Boolean(uid && token);

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setErrorMessage("Email is required.");
      return;
    }

    if (!EMAIL_RULE.test(normalizedEmail)) {
      setErrorMessage("Enter a valid email address.");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await requestCustomerPasswordReset({ email: normalizedEmail });
      setSuccessMessage(payload.message || "If an account exists for this email, a reset link has been sent.");
      setEmail(normalizedEmail);
    } catch (requestError) {
      setErrorMessage(requestError.message || "Unable to start password reset right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!uid || !token) {
      setErrorMessage("Reset link is invalid. Please request a new one.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setErrorMessage("New password and confirm password are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("New password and confirm password must match.");
      return;
    }

    if (!PASSWORD_RULE.test(newPassword)) {
      setErrorMessage("Password must be at least 8 characters and include letters and numbers.");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await confirmCustomerPasswordReset({
        uid,
        token,
        newPassword,
        confirmPassword,
      });
      setSuccessMessage(payload.message || "Password reset successfully. Please sign in with your new password.");
      setNewPassword("");
      setConfirmPassword("");
      window.setTimeout(() => {
        navigate("/sign-in");
      }, 1200);
    } catch (confirmError) {
      setErrorMessage(confirmError.message || "Unable to reset password right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">{hasResetToken ? "Set a new password" : "Forgot your password?"}</Heading>
              <Text color="gray.600" fontSize="sm">
                {hasResetToken
                  ? "Enter and confirm your new password to continue."
                  : "Enter your account email and we will send you a password reset link."}
              </Text>
            </VStack>

            <form onSubmit={hasResetToken ? handleConfirmReset : handleRequestReset}>
              <VStack spacing={4} align="stretch">
                {!hasResetToken ? (
                  <FormControl isRequired isInvalid={Boolean(errorMessage && (errorMessage.toLowerCase().includes("email") || errorMessage.toLowerCase().includes("valid")))}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    />
                    <FormErrorMessage>
                      {errorMessage.toLowerCase().includes("email") || errorMessage.toLowerCase().includes("valid") ? errorMessage : ""}
                    </FormErrorMessage>
                  </FormControl>
                ) : (
                  <>
                    <FormControl isRequired isInvalid={Boolean(errorMessage && (errorMessage.toLowerCase().includes("password") || errorMessage.toLowerCase().includes("match")))}>
                      <FormLabel>New password</FormLabel>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        bg="white"
                        h="52px"
                        borderRadius="lg"
                        borderColor="gray.300"
                        _focus={{ borderColor: "black", boxShadow: "none" }}
                        _hover={{ borderColor: "gray.400" }}
                        required
                      />
                    </FormControl>
                    <FormControl isRequired isInvalid={Boolean(errorMessage && (errorMessage.toLowerCase().includes("password") || errorMessage.toLowerCase().includes("match")))}>
                      <FormLabel>Confirm new password</FormLabel>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        bg="white"
                        h="52px"
                        borderRadius="lg"
                        borderColor="gray.300"
                        _focus={{ borderColor: "black", boxShadow: "none" }}
                        _hover={{ borderColor: "gray.400" }}
                        required
                      />
                      <FormErrorMessage>
                        {errorMessage.toLowerCase().includes("password") || errorMessage.toLowerCase().includes("match") ? errorMessage : ""}
                      </FormErrorMessage>
                    </FormControl>
                  </>
                )}

                {successMessage && (
                  <Text color="green.600" fontSize="sm">
                    {successMessage}
                  </Text>
                )}

                {errorMessage && !errorMessage.toLowerCase().includes("email") && !errorMessage.toLowerCase().includes("valid") && !errorMessage.toLowerCase().includes("password") && !errorMessage.toLowerCase().includes("match") && (
                  <Text color="red.500" fontSize="sm">
                    {errorMessage}
                  </Text>
                )}

                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText={hasResetToken ? "Resetting" : "Sending"}
                  bg="black"
                  color="white"
                  borderRadius="full"
                  h="50px"
                  _hover={{ bg: "gray.900" }}
                >
                  {hasResetToken ? "Reset password" : "Send reset link"}
                </Button>

                <Text fontSize="sm" color="gray.700" textAlign="center">
                  <Link as={RouterLink} to="/sign-in" color="blue.500" textDecoration="underline" _hover={{ color: "blue.600" }}>
                    Back to sign in
                  </Link>
                </Text>
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
